import "./compliance.css";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Drawer, Input, InputNumber, Segmented } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faUsers } from "@fortawesome/free-solid-svg-icons";
import { useMutation } from "@apollo/client";
import { useDispatch } from "react-redux";
import Spinner from "@src/components/spinner";
import { setNotification } from "@src/middleware/redux/reducers/notification.reducer";
import { newToolboxTalkMutation, publishToolboxTalkMutation, updateToolboxTalkMutation } from "@src/util/mutation/compliance.mutation";
import { AudiencePickerModal } from "./audiencePicker.modal";
import { describeAssignment, emptyAudience, hasAudience, RECURRENCE_LABEL, summariseAudience } from "./complianceHelpers";

/**
 * Write (or edit) a Toolbox Talk: WFC360 turns the text - plus an optional PDF/image - into
 * the issue officers acknowledge, posts it (pinned) to their feed, and re-issues it on the
 * chosen schedule. Editing changes the text/schedule for the next issue; the current issue
 * stays as sent.
 */
export const ToolboxTalkDrawer = ({ visible, onClose, onSaved = () => {}, editTalk = null }) => {
    const dispatch = useDispatch();
    const fileRef = useRef();
    const editing = !!editTalk;

    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");
    const [attachment, setAttachment] = useState(null);
    const [audience, setAudience] = useState(emptyAudience());
    const [audienceOpen, setAudienceOpen] = useState(false);
    const [recurrence, setRecurrence] = useState("none");
    const [method, setMethod] = useState("tick");
    const [dueInDays, setDueInDays] = useState(3);
    const [saving, setSaving] = useState(false);

    const [newTalk] = useMutation(newToolboxTalkMutation);
    const [publishTalk] = useMutation(publishToolboxTalkMutation);
    const [updateTalk] = useMutation(updateToolboxTalkMutation);

    useEffect(() => {
        if (!visible) return;
        setTitle(editTalk?.title || "");
        setContent(editTalk?.talkContent || "");
        setAttachment(null);
        setAudience({ ...emptyAudience(), ...(editTalk?.audience || {}) });
        setRecurrence(editTalk?.recurrence || "none");
        setMethod(editTalk?.acknowledgementMethod || "tick");
        setDueInDays(editTalk?.dueInDays ?? 3);
    }, [visible, editTalk]);

    const missing = [];
    if (!title.trim()) missing.push("a title");
    if (!content.trim() && !attachment && !editing) missing.push("the talk (or an attachment)");
    if (!hasAudience(audience)) missing.push("who must acknowledge it");

    const save = async () => {
        if (missing.length) return;
        setSaving(true);
        try {
            if (editing) {
                const res = await updateTalk({
                    variables: {
                        documentId: editTalk._id,
                        input: { title: title.trim(), content, audience, recurrence, acknowledgementMethod: method, dueInDays },
                    },
                });
                const result = res?.data?.updateToolboxTalk?.response;
                const changed = (result?.assigned || 0) + (result?.reopened || 0) + (result?.revoked || 0);
                dispatch(setNotification({ error: false, notificationState: true, message: changed ? `Saved - ${describeAssignment(result).toLowerCase()}` : "Saved - changes apply from the next issue" }));
            } else {
                const res = await newTalk({
                    variables: {
                        input: {
                            title: title.trim(), content, audience, recurrence, acknowledgementMethod: method, dueInDays,
                            attachmentFileName: attachment?.name || null,
                        },
                    },
                });
                const { documentId, attachmentUploadUrl } = res?.data?.newToolboxTalk?.response || {};
                if (attachment && attachmentUploadUrl) {
                    await axios.put(attachmentUploadUrl, attachment, { headers: { "Content-Type": attachment.type || "application/octet-stream" } });
                }
                const published = await publishTalk({ variables: { documentId } });
                const result = published?.data?.publishToolboxTalk?.response;
                dispatch(setNotification({ error: false, notificationState: true, message: `${title.trim()} published - ${describeAssignment(result).toLowerCase()}` }));
            }
            onSaved();
            onClose();
        } catch (err) {
            dispatch(setNotification({ error: true, notificationState: true, message: err.message }));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Drawer
            title={editing ? `Edit ${editTalk?.title || "toolbox talk"}` : "New toolbox talk"}
            placement="right"
            open={visible}
            onClose={saving ? undefined : onClose}
            width={640}
            maskClosable={false}
            closeIcon={<FontAwesomeIcon icon={faClose} className="close-icon" />}
            footer={
                <div className="drawer-footer" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <button className={saving || missing.length ? "disabled-btn drawer-filled-btn" : "drawer-filled-btn"} disabled={saving || missing.length > 0} onClick={save}>
                        {saving ? <Spinner /> : editing ? "Save" : "Publish"}
                    </button>
                    <button className="drawer-outlined-btn" disabled={saving} onClick={onClose}>Cancel</button>
                    {missing.length > 0 && <span className="cmp-hint">Add {missing.join(", ")}</span>}
                </div>
            }
        >
            <div className="cmp-field">
                <label>Title</label>
                <Input className="generic-input-control" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Slips, trips and falls" />
            </div>

            <div className="cmp-field">
                <label>The talk</label>
                <Input.TextArea
                    className="generic-input-control"
                    rows={9}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder={"What officers need to know. Leave a blank line between paragraphs."}
                />
                <span className="cmp-hint">
                    {editing ? "Changes go out with the next issue - the current one stays as sent." : "Officers read this in the app, and it's what they acknowledge."}
                </span>
            </div>

            {!editing && (
                <div className="cmp-field">
                    <label>Attachment (optional)</label>
                    <div className="cmp-dropzone" onClick={() => fileRef.current?.click()}>
                        {attachment
                            ? <><div className="cmp-dropzone-file">{attachment.name}</div><div className="cmp-hint">Click to choose a different file</div></>
                            : "Add a PDF or image - it's added after the talk text"}
                    </div>
                    <input type="file" ref={fileRef} style={{ display: "none" }} accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => { if (e.target.files?.[0]) setAttachment(e.target.files[0]); e.target.value = ""; }} />
                </div>
            )}

            <div className="cmp-field">
                <label>Who must acknowledge it</label>
                <button type="button" className="cmp-choice-card" onClick={() => setAudienceOpen(true)}>
                    <div className="cmp-choice-card-title"><FontAwesomeIcon icon={faUsers} style={{ marginRight: 6, color: "#00a4bd" }} />{summariseAudience(audience)}</div>
                    <div className="cmp-choice-card-text">Everyone, branches, sites, site groups, customers, suppliers or individual employees</div>
                </button>
            </div>

            <div className="cmp-field">
                <label>Repeat</label>
                <Segmented
                    value={recurrence}
                    onChange={setRecurrence}
                    options={Object.entries(RECURRENCE_LABEL).map(([value, label]) => ({ value, label }))}
                />
                <span className="cmp-hint">
                    {recurrence === "none" ? "Sent once." : `A new issue goes out ${RECURRENCE_LABEL[recurrence].toLowerCase()} and everyone acknowledges it again.`}
                </span>
            </div>

            <div style={{ display: "flex", gap: 16 }}>
                <div className="cmp-field" style={{ flex: 1 }}>
                    <label>How officers confirm</label>
                    <Segmented
                        value={method}
                        onChange={setMethod}
                        options={[{ value: "tick", label: "Tick box + name" }, { value: "signature", label: "Drawn signature" }]}
                    />
                </div>
                <div className="cmp-field" style={{ flex: 1 }}>
                    <label>Days to acknowledge</label>
                    <InputNumber min={0} max={365} precision={0} value={dueInDays} onChange={(v) => setDueInDays(v === null ? 0 : v)} className="generic-input-control" style={{ width: "100%" }} />
                </div>
            </div>

            <AudiencePickerModal visible={audienceOpen} value={audience} onClose={() => setAudienceOpen(false)} onChange={setAudience} />
        </Drawer>
    );
};
